import { useState } from 'react';
import { ChevronDown, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FaqCategory } from '../content';

/**
 * One FAQ category — a white card with the section heading and an accordion
 * of its questions. Rows start collapsed; each toggles independently. An
 * entry with a `videoUrl` gets a "Watch video" link under its answer that
 * opens in a new tab (outbound link — no embedded player).
 */
export function FaqCategoryCard({ category }: { category: FaqCategory }) {
  // Independent open state per row, keyed by index within the category.
  const [open, setOpen] = useState<Set<number>>(new Set());

  const toggle = (index: number) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <section
      id={category.id}
      aria-labelledby={`${category.id}-heading`}
      className="bg-white border border-line rounded-2xl px-6 sm:px-7 py-5 shadow-card scroll-mt-6"
    >
      <h2 id={`${category.id}-heading`} className="text-[15px] font-semibold text-ink m-0 py-1">
        {category.title}
      </h2>
      <div className="divide-y divide-line">
        {category.entries.map((entry, index) => {
          const isOpen = open.has(index);
          const answerId = `${category.id}-answer-${index}`;
          return (
            <div key={entry.question}>
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={answerId}
                onClick={() => toggle(index)}
                className="w-full flex items-start justify-between gap-4 py-4 bg-transparent border-0 cursor-pointer text-left group"
              >
                <span className="text-[14px] font-medium text-ink group-hover:text-navy">
                  {entry.question}
                </span>
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    'size-4 shrink-0 mt-0.5 text-muted transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
              {isOpen && (
                <div id={answerId} className="pb-4 pr-8">
                  <p className="text-[14px] leading-relaxed text-muted m-0">{entry.answer}</p>
                  {entry.videoUrl && (
                    <a
                      href={entry.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-medium text-electric no-underline hover:underline"
                    >
                      <Play className="size-3.5" aria-hidden="true" />
                      Watch video
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
